/* eslint-disable react/no-unescaped-entities */
import { useState } from "react";
import { api } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import { BaobabMotif } from "./BaobabTree";
import { Anchor, Shield, GitFork, Heart, Users, ChevronRight, Sprout } from "lucide-react";

const STEPS = [
  { Icon: Anchor, titre: "Les racines — le savoir", ln: "Misisa — mayele", texte: "Bombacare s'enracine dans la sagesse médicale et le soin communautaire : protocoles de rééducation, gestes du quotidien, expérience des familles." },
  { Icon: Shield, titre: "Le tronc — la plateforme", ln: "Nzete — plateforme", texte: "Un tronc solide qui porte tout le réseau, disponible en ligne comme hors ligne. Comme le baobab en saison sèche, il tient." },
  { Icon: GitFork, titre: "Les branches — les praticiens", ln: "Bitape — ba praticiens", texte: "Chaque praticien est un Centre Bombacare : une branche vivante, autonome, à la fois ambassadrice et bénéficiaire de la vision." },
  { Icon: Heart, titre: "Les fruits — la guérison", ln: "Mbuma — kobika", texte: "Au bout des branches, les patients et leurs proches : chaque séance, chaque mesure, chaque encouragement porte ses fruits." },
  { Icon: Users, titre: "L'arbre à palabre — la transmission", ln: "Nzete ya palabre — koteya", texte: "Ici le savoir circule : concertation entre praticiens, parrainage de nouveaux centres, bonnes pratiques partagées." },
];

export function BaobabOnboarding({ me, onDone }) {
  const { setUser, user } = useAuth();
  const [i, setI] = useState(0);
  const [closing, setClosing] = useState(false);
  const last = i === STEPS.length;
  const finish = async () => {
    setClosing(true);
    try { await api.post("/network/onboarding-seen"); } catch { /* hors-ligne : on mémorise localement */ }
    localStorage.setItem("bc_baobab_onboarding", "1");
    if (user) setUser({ ...user, onboarding_baobab_vu: true });
    onDone?.();
  };
  const s = STEPS[Math.min(i, STEPS.length - 1)];
  return (
    <div data-testid="baobab-onboarding" className={`fixed inset-0 z-[60] bb-hero bb-grain flex flex-col overflow-y-auto overflow-x-hidden transition-opacity duration-500 ${closing ? "opacity-0" : "opacity-100"}`}>
      <BaobabMotif className="bb-motif !w-[520px] !opacity-[0.14]" />
      <div className="relative flex-1 flex flex-col max-w-2xl w-full mx-auto p-6 md:p-10">
        <div className="flex items-center justify-between">
          <div className="text-[11px] uppercase tracking-[0.2em] text-white/70 font-bold">Vision Baobab</div>
          <button data-testid="onboarding-skip" onClick={finish} className="text-[12px] text-white/70 underline">Passer</button>
        </div>
        <div className="flex gap-1.5 mt-5" data-testid="onboarding-progress">
          {STEPS.map((_, k) => <span key={k} className={`h-1.5 flex-1 rounded-full transition-colors ${k <= i ? "bg-[#D4AF5A]" : "bg-white/20"}`} />)}
        </div>
        <div className="flex-1 flex flex-col justify-center py-8">
          {!last ? (
            <div key={i} className="bb-rise">
              <span className="w-16 h-16 rounded-3xl bg-white/10 backdrop-blur-md border border-white/15 grid place-items-center"><s.Icon size={30} className="text-[#D4AF5A]" /></span>
              <div className="text-[12px] text-[#D4AF5A] font-bold mt-6">{s.ln}</div>
              <h2 data-testid="onboarding-title" className="font-[Fraunces] text-3xl md:text-5xl font-bold leading-[1.05] mt-1">{s.titre}</h2>
              <p className="text-[15px] md:text-lg text-white/85 leading-relaxed mt-5 max-w-xl">{s.texte}</p>
            </div>
          ) : (
            <div className="bb-rise">
              <span className="w-16 h-16 rounded-3xl bg-[#D4AF5A] text-[#0A1426] grid place-items-center"><Sprout size={30} /></span>
              <div className="text-[12px] text-[#D4AF5A] font-bold mt-6">Boyei malamu — bienvenue</div>
              <h2 data-testid="onboarding-title" className="font-[Fraunces] text-3xl md:text-5xl font-bold leading-[1.05] mt-1">Vous êtes une nouvelle branche.</h2>
              <p className="text-[15px] md:text-lg text-white/85 leading-relaxed mt-5 max-w-xl">
                <b>{me?.nom || "Votre Centre Bombacare"}</b>{me?.ville ? ` (${me.ville})` : ""} rejoint le baobab{me?.parrain ? `, rattaché à ${me.parrain.nom}` : ""}.
                Vos patients en sont les feuilles ; votre savoir, la sève qui circule vers les autres centres.
              </p>
            </div>
          )}
        </div>
        <div className="flex items-center justify-between gap-3">
          <button data-testid="onboarding-prev" onClick={() => setI((x) => Math.max(0, x - 1))} disabled={i === 0} className="h-12 px-5 rounded-full border border-white/25 text-white text-[13px] font-bold disabled:opacity-30">Précédent</button>
          {!last
            ? <button data-testid="onboarding-next" onClick={() => setI((x) => x + 1)} className="h-12 px-6 rounded-full bg-[#D4AF5A] text-[#0A1426] text-[14px] font-bold inline-flex items-center gap-1">Suivant <ChevronRight size={16} /></button>
            : <button data-testid="onboarding-finish" onClick={finish} className="h-12 px-6 rounded-full bg-white text-[#0A1426] text-[14px] font-bold inline-flex items-center gap-1">Entrer dans mon centre <ChevronRight size={16} /></button>}
        </div>
      </div>
    </div>
  );
}
